import React, { useEffect, useRef, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik, FormikProps } from "formik";
import * as yup from "yup";
import { get } from "lodash";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputField } from "../../../../components/Shared/InputField";
import { request } from "../../../../utils/Request";
import { Divider } from "primereact/divider";

interface IProps {
    visible: boolean;
    toastify: any;
    damageCase: any;
    onClose: any;
    status?: string; // 'open', 'in-progress', 'closed', 'archived'
}

type TInputForm = {
    cost: string | null;
};

const DEFAULT_FORM_STATE = {
    cost: null,
};

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        cost: yup.string().trim().required(t('the field is required.', { field: t('cost') })).nullable(),
    });
};

function OpenOrderDamageCaseCostUpdate(props: IProps) {
    const { toastify, damageCase, status = 'open' } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<TInputForm>(DEFAULT_FORM_STATE);
    const [open, setOpen] = useState<boolean>(false);
    const { t }: any = useTranslation();

    useEffect(() => {
        if ( props.visible && damageCase ) {
            setOpen(true);
            // Initialize with existing cost if available
            const existingCost = get(damageCase, ['cost']);
            setInitFormState({
                cost: existingCost ? String(existingCost) : null,
            });
        } else if ( !props.visible ) {
            setOpen(false);
        }
    }, [props.visible, damageCase]);

    const onClose = () => {
        setOpen(false);
        props.onClose();
    };

    const onSubmit = async (payloads: TInputForm) => {
        const damageCaseId = get(damageCase, ['id']);
        if ( !damageCaseId ) {
            toastify(t("server error"), "error");
            return;
        }

        try {
            await request({
                method: 'PATCH',
                url: `/suppliers/order/damage/cases/open/${ damageCaseId }`,
                data: {
                    cost: payloads.cost,
                },
            });

            toastify(t("record updated", { item: t('damage case') }), "info");
            onClose();
        } catch (e: any) {
            if ( e.status === 422 && e.data && e.data.errors ) {
                toastify(t("validation error"), "error");
            } else if ( e.status === 404 ) {
                toastify(t("action not allowed: 404"), "error");
            } else {
                toastify(t("server error"), "error");
            }
        }
    };

    const onReset = () => {
        const base: any = formRef.current;
        if ( base ) {
            base.resetForm();
        }
        const existingCost = get(damageCase, ['cost']);
        setInitFormState({
            cost: existingCost ? String(existingCost) : null,
        });
    };

    const header = (
        <div className="bg-body-tertiary card-header">
            <h5 className="card-title mt-0">
                { get(damageCase, ['case_number']) ? t('update cost') + ' #' + get(damageCase, ['case_number']) : t('update cost') }
            </h5>
        </div>
    );

    return (
        <>
            <Dialog
                header={ header }
                visible={ open }
                onHide={ onClose }
                style={ { width: '50vw' } }
                modal={ true }
                draggable={ false }
                resizable={ false }
                closeOnEscape={ true }
            >
                <Formik
                    innerRef={ formRef }
                    enableReinitialize={ true }
                    initialValues={ initFormState }
                    onSubmit={ onSubmit }
                    validationSchema={ validationSchema(t) }
                >
                    { (formProps: FormikProps<TInputForm>) => {
                        return (
                            <Form onSubmit={ formProps.handleSubmit } className="mb-5">
                                <Row className="mt-4 md:mb-4 sm:mb-4">
                                    <Col sm={ 12 }>
                                        <Field
                                            component={ InputField }
                                            name="cost"
                                            label={ t("cost") }
                                            placeholder={ t("enter") + ' ' + t("cost").toLowerCase() }
                                            isRequired
                                            autoFocus
                                        />
                                    </Col>
                                </Row>

                                <div>
                                    <Divider />
                                    <div className="mt-2 text-end">
                                        <Button
                                            type="submit"
                                            className={ "mr-2" }
                                            disabled={ formProps.isSubmitting }
                                            tooltip={ t("to data save into database") }
                                            size="small"
                                            outlined
                                            tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-save me-2" />
                                            { t("save") }
                                        </Button>
                                        <Button
                                            type="button"
                                            className={ "mr-2" }
                                            disabled={ formProps.isSubmitting }
                                            onClick={ onReset }
                                            tooltip={ t("to make all fields empty") }
                                            size="small"
                                            severity="warning"
                                            outlined
                                            tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-refresh me-2" />
                                            { t("reset") }
                                        </Button>
                                        <Button
                                            type="button"
                                            className={ "mr-2" }
                                            tooltip={ t("operation cancel") }
                                            size="small"
                                            severity="danger"
                                            outlined
                                            tooltipOptions={ { position: 'top' } }
                                            onClick={ onClose }
                                        >
                                            <i className="pi pi-arrow-left me-2" />
                                            { t("cancel") }
                                        </Button>
                                    </div>
                                </div>
                            </Form>
                        );
                    } }
                </Formik>
            </Dialog>
        </>
    );
}

export default withTranslation()(OpenOrderDamageCaseCostUpdate);

